import * as React from 'react'
import { AlertTriangle, Check, Copy, ExternalLink, KeyRound, Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useFinishStripeLink, useIssueBillingKey, useRevokeBillingKey } from '@/queries/billing'
import { RagApiError } from '@/services/ApiService'
import { billingService } from '@/services/billingService'
import type { BillingAccount } from '@/types/billing'

/** Where a seller can open its Stripe account. Stripe needs it before anything else. */
const SELLER_COUNTRIES = ['ES', 'CH']

/** The reason the server gave, which for a Stripe refusal is Stripe's own (R34). */
function reasonOf(error: unknown): string | null {
  if (!(error instanceof RagApiError)) return null
  const body = error.body as { error?: { message?: unknown } } | null | undefined
  return typeof body?.error?.message === 'string' ? body.error.message : null
}

/** This page without its query: Stripe must send the seller back to a registered address. */
function pageWithoutQuery(): string {
  return `${window.location.origin}${window.location.pathname}`
}

/** Back from Stripe after linking an existing account: `?code=&state=`, or `?error=` if it was refused. */
function useStripeLinkReturn() {
  const { t } = useTranslation()
  const finish = useFinishStripeLink()
  const done = React.useRef(false)

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const refused = params.get('error_description') ?? params.get('error')
    if (done.current || (!refused && !(code && state))) return
    done.current = true
    // The code works once: drop it from the address before anything can reuse it.
    window.history.replaceState(null, '', pageWithoutQuery())
    if (refused) {
      toast.error(t('billing.payments.link_failed', { reason: refused }))
      return
    }
    finish.mutateAsync({ code: code as string, state: state as string })
      .then(() => toast.success(t('billing.payments.linked')))
      .catch((error: unknown) => toast.error(t('billing.payments.link_failed', { reason: reasonOf(error) ?? t('billing.payments.connect_failed') })))
  }, [finish, t])
}

/** Payments connection and server keys: how money and apps reach the account. */
export function AccessSection({ account, revealKey }: { account: BillingAccount; revealKey: string | null }) {
  const { t, i18n } = useTranslation()
  const issue = useIssueBillingKey()
  const revoke = useRevokeBillingKey()
  const [shown, setShown] = React.useState<string | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [toRevoke, setToRevoke] = React.useState<string | null>(null)
  const [connecting, setConnecting] = React.useState(false)
  const [connectError, setConnectError] = React.useState<string | null>(null)
  const [country, setCountry] = React.useState(SELLER_COUNTRIES[0])
  const countryName = React.useMemo(() => new Intl.DisplayNames([i18n.language], { type: 'region' }), [i18n.language])

  useStripeLinkReturn()

  React.useEffect(() => {
    if (revealKey) setShown(revealKey)
  }, [revealKey])

  const issueKey = async () => {
    try {
      const { key } = await issue.mutateAsync({ id: account.id, label: 'studio' })
      setCopied(false)
      setShown(key)
    } catch {
      toast.error(t('billing.errors.save'))
    }
  }

  const confirmRevoke = async () => {
    if (!toRevoke) return
    try {
      await revoke.mutateAsync({ id: account.id, prefix: toRevoke })
      toast.success(t('billing.keys.revoked'))
    } catch {
      toast.error(t('billing.errors.save'))
    } finally {
      setToRevoke(null)
    }
  }

  const openStripe = async (link: () => Promise<string>) => {
    setConnecting(true)
    setConnectError(null)
    try {
      window.location.assign(await link())
    } catch (error) {
      setConnectError(reasonOf(error) ?? t('billing.payments.connect_failed'))
      setConnecting(false)
    }
  }
  const connect = () => openStripe(() => billingService.connectPayments(account.id, window.location.href, country))
  const linkExisting = () => openStripe(() => billingService.linkExistingPayments(account.id, pageWithoutQuery()))

  const payment = account.payment_account
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">{t('billing.access.title')}</h2>
      <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
        <div className="space-y-1">
          <p className="font-medium">{t('billing.payments.title')}</p>
          <p className="text-xs text-muted-foreground">
            {!payment ? t('billing.payments.not_connected')
              : payment.can_charge ? t('billing.payments.ready', { id: payment.external_id })
                : t('billing.payments.pending', { id: payment.external_id })}
          </p>
        </div>
        {!payment?.can_charge && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {!payment && (
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="w-40" aria-label={t('billing.payments.country')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SELLER_COUNTRIES.map((code) => (
                      <SelectItem key={code} value={code}>{countryName.of(code)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => void connect()} disabled={connecting}>
                <ExternalLink className="mr-2 h-4 w-4" />
                {payment ? t('billing.payments.continue') : t('billing.payments.connect')}
              </Button>
              {!payment && (
                <Button variant="outline" size="sm" onClick={() => void linkExisting()} disabled={connecting}>
                  <Link2 className="mr-2 h-4 w-4" />
                  {t('billing.payments.link_existing')}
                </Button>
              )}
            </div>
            {connectError && <p role="alert" className="text-sm text-destructive">{connectError}</p>}
          </div>
        )}
      </div>
      <div className="space-y-2 border-t pt-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="font-medium">{t('billing.keys.title')}</p>
            <p className="text-xs text-muted-foreground">{t('billing.keys.help')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void issueKey()} disabled={issue.isPending}>
            <KeyRound className="mr-2 h-4 w-4" />
            {t('billing.keys.issue')}
          </Button>
        </div>
        {account.keys.map((key) => (
          <div key={key.prefix} className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <p className="font-mono text-sm">{t('billing.keys.masked', { prefix: key.prefix })}</p>
              {key.label && <Badge variant="outline">{key.label}</Badge>}
            </div>
            <Button variant="ghost" size="xs" className="text-destructive" onClick={() => setToRevoke(key.prefix)}>
              {t('billing.keys.revoke')}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={shown !== null} onOpenChange={(next) => !next && setShown(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('billing.keys.shown_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="border-warning/30 bg-warning/10">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle>{t('billing.keys.once_title')}</AlertTitle>
              <AlertDescription>{t('billing.keys.once_description')}</AlertDescription>
            </Alert>
            <div className="space-y-1">
              <Label htmlFor="billing-key-value">{t('billing.keys.value')}</Label>
              <Textarea id="billing-key-value" aria-label={t('billing.keys.value')} value={shown ?? ''} readOnly rows={2} className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShown(null)}>{t('billing.common.close')}</Button>
            <Button onClick={() => { void navigator.clipboard.writeText(shown ?? ''); setCopied(true) }}>
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? t('billing.keys.copied') : t('billing.keys.copy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={toRevoke !== null} onOpenChange={(next) => !next && setToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('billing.keys.revoke_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('billing.keys.revoke_description', { prefix: toRevoke ?? '' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('billing.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmRevoke()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('billing.keys.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
